/**
 * User data model types and interfaces for Heapster ID
 * Based on PRD specifications for Firestore user collection structure
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Display name source enumeration to track how the display name was set.
 * Used to distinguish between auto-generated and user-provided display names.
 */
export const DisplayNameSource = {
  /** User manually provided the display name */
  USER: 'user',

  /** Display name was generated from email prefix */
  EMAIL: 'email',

  /** Display name was auto-generated as fallback (User{uid}) */
  AUTO: 'auto',
} as const;

export type DisplayNameSource =
  (typeof DisplayNameSource)[keyof typeof DisplayNameSource];

// Photo URL structure with multiple sizes
export interface PhotoUrls {
  128?: string; // 128px JPEG version
  512?: string; // 512px JPEG version
}

/**
 * Core user document interface (/users/{uid}).
 * Represents the user profile data as stored in Firestore.
 */
export interface UserDocument {
  /** Firebase Authentication UID (Primary Key) */
  uid: string;

  /** Required display name (no uniqueness constraint) */
  display_name: string;

  /** Source of the display name (user-provided vs auto-generated) */
  display_name_source: DisplayNameSource;

  /** Email verification status (required for subscriptions) */
  email_verified: boolean;

  /** Profile photo URLs in multiple sizes */
  photo_url?: PhotoUrls | null;

  /** Stripe customer ID for subscription management (optional) */
  stripe_customer_id?: string;

  /** Account creation timestamp */
  created_at: Timestamp;

  /** Last update timestamp */
  updated_at?: Timestamp;
}

/**
 * User profile data for frontend use (with serialized timestamps).
 * This is the same as UserDocument but with Date objects instead of Firestore Timestamps.
 */
export interface UserProfile {
  uid: string;
  email?: string;
  display_name: string;
  display_name_source: DisplayNameSource;
  email_verified: boolean;
  photo_url?: PhotoUrls | null;

  /** Stripe customer ID for subscription management (optional) */
  stripe_customer_id?: string;

  /**
   * Badge IDs the user has actually been granted. Since #1959 there is no
   * read-time derivation from entitlements: holding `sys:admin` no longer adds
   * a badge here. Presentation data (name, icon) lives in the badge catalogue —
   * see `useUserBadgeDisplay` for the join.
   */
  badges?: string[];

  created_at: Date;
  updated_at?: Date;
}

// User badge interface (/users/{uid}/badges/{badgeId})
export interface UserBadge {
  badgeId: string; // Reference to /badges/{badgeId}
  earned_at: Timestamp; // When the badge was earned
}

// User interaction interface (/users/{uid}/interactions/{interactionId})
export interface UserInteraction {
  interactionId: string; // Reference to /interactions/{interactionId}
  recorded_at: Timestamp; // When the interaction was recorded
}

// Badge definition interface (/badges/{badgeId})
export interface BadgeDefinition {
  badgeId: string; // Primary Key
  name: string; // Badge name
  icon_url: string; // URL to badge icon
  description: string; // Badge description
  created_at: Timestamp; // Badge creation timestamp
}

// Interaction definition interface (/interactions/{interactionId})
export interface InteractionDefinition {
  interactionId: string; // Primary Key
  type: InteractionType; // Interaction type
  title: string; // Interaction title
  description: string; // Interaction description
  occurred_at: Timestamp; // When the interaction occurred
  created_at: Timestamp; // When the record was created
}

// Interaction type enumeration
export const InteractionType = {
  VIDEO_CALL: 'VIDEO_CALL',
  LIVE_CHAT: 'LIVE_CHAT',
  MEETUP: 'MEETUP',
  WORKSHOP: 'WORKSHOP',
  CONFERENCE: 'CONFERENCE',
  PRIVATE_SESSION: 'PRIVATE_SESSION',
} as const;

export type InteractionType =
  (typeof InteractionType)[keyof typeof InteractionType];

// Subscription status enumeration
export const SubscriptionStatus = {
  ACTIVE: 'ACTIVE',
  TRIALING: 'TRIALING',
  GRACE_PERIOD: 'GRACE_PERIOD',
  PAST_DUE: 'PAST_DUE',
  CANCELED: 'CANCELED',
  PAUSED: 'PAUSED',
} as const;

export type SubscriptionStatus =
  (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

// Subscription document interface (/entitlements/{uid})
export interface SubscriptionDocument {
  uid: string; // Primary Key (matches user UID)
  status: SubscriptionStatus; // Current subscription status
  tier: string; // Subscription tier/plan identifier
  current_period_end: Timestamp; // End of current billing period
  created_at: Timestamp; // Subscription creation timestamp
  updated_at: Timestamp; // Last update timestamp
}

// Subscription data for frontend use (with serialized timestamps)
export interface UserSubscription {
  uid: string;
  status: SubscriptionStatus;
  tier: string;
  current_period_end: Date;
  created_at: Date;
  updated_at: Date;
}

// User update input (for profile updates)
export interface UpdateUserInput {
  display_name?: string; // Update display name
  display_name_source?: DisplayNameSource; // Update display name source
  photo_url?: PhotoUrls | null; // Update profile photo
}

/**
 * Admin user update input.
 * Used when administrators update other users' profiles.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AdminUpdateUserInput extends UpdateUserInput {}

// User query parameters for search
export interface UserQueryParams {
  query?: string; // Search query (email, display_name)
  limit?: number; // Number of results to return
  offset?: string; // Pagination offset (document ID)
}

// User search result
export interface UserSearchResult {
  users: UserProfile[];
  total: number; // Total matching users
  hasMore: boolean; // Whether there are more results
  nextOffset?: string; // Next pagination offset
}

export const isSubscriptionStatus = (
  value: string
): value is SubscriptionStatus => {
  return Object.values(SubscriptionStatus).includes(
    value as SubscriptionStatus
  );
};

export const isInteractionType = (value: string): value is InteractionType => {
  return Object.values(InteractionType).includes(value as InteractionType);
};

export const isDisplayNameSource = (
  value: string
): value is DisplayNameSource => {
  return Object.values(DisplayNameSource).includes(value as DisplayNameSource);
};

// Helper function to convert Firestore document to UserProfile
export const firestoreToUserProfile = (doc: UserDocument): UserProfile => {
  return {
    uid: doc.uid,
    display_name: doc.display_name,
    display_name_source: doc.display_name_source,
    email_verified: doc.email_verified,
    photo_url: doc.photo_url,
    stripe_customer_id: doc.stripe_customer_id,
    created_at: doc.created_at.toDate(),
    updated_at: doc.updated_at?.toDate(),
  };
};

// Helper function to convert UserProfile to Firestore document format
export const userProfileToFirestore = (
  profile: UserProfile
): Omit<UserDocument, 'created_at' | 'updated_at'> & {
  created_at?: Timestamp;
  updated_at?: Timestamp;
} => {
  return {
    uid: profile.uid,
    display_name: profile.display_name,
    display_name_source: profile.display_name_source,
    email_verified: profile.email_verified,
    photo_url: profile.photo_url,
    stripe_customer_id: profile.stripe_customer_id,
    ...(profile.created_at && {
      created_at: Timestamp.fromDate(profile.created_at),
    }),
    ...(profile.updated_at && {
      updated_at: Timestamp.fromDate(profile.updated_at),
    }),
  };
};
